import { FiArchive, FiUpload } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toastSuccess } from '../utils/toasts';
import AdminSectionTitle from '../components/AdminSectionTitle';
import FilterTable, { BaseData } from '../components/Table/Table';
import Button from '../components/atoms/Button';
import { useGetListGuestsQuery, useCreateListGuestsMutation } from '../store';
import EmptyTable from '../components/Table/EmptyTable';
import CustomModal from '../components/atoms/CustomModal';
import FormControlTextArea from '../components/atoms/FormControlTextArea';
import Input from '../components/atoms/Input';
import LoadingLottie from '../components/atoms/LoadingLottie';
import RequireAuth from '../store/utils/RequireAuth';

interface FormData {
  guests: string;
  related_person: string | undefined;
}

interface Row extends BaseData {
  name: string;
}

const EventListGuests = () => {
  const { list_id: listId } = useParams();
  const { data: listGuests, isLoading: isLoadingListGuests } = useGetListGuestsQuery(listId || '');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createListGuests, { isLoading: isCreatingListGuests, isSuccess: isCreateListGuestsSuccess }] = useCreateListGuestsMutation();

  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      guests: '',
      related_person: '',
    },
    resolver: yupResolver(yup.object().shape({
      guests: yup.string().required('Los invitados son requeridos'),
      related_person: yup.string().optional(),
    })),
  });

  useEffect(() => {
    if (isCreateListGuestsSuccess) {
      setIsModalOpen(false);
      reset();
      toastSuccess('Invitados creados correctamente');
    }
  }, [isCreateListGuestsSuccess]);

  const handleSubmitAddGuests = (data: FormData) => {
    createListGuests({
      list_id: listId || '',
      names_text: data.guests,
      related_person: data.related_person,
    });
  };

  const rows: Row[] = listGuests?.map((guest) => ({
    id: guest.id,
    name: guest.name,
    related_person: guest.related_person,
  })) || [];

  const headCells = [
    { id: 'name', label: 'Nombre' },
    { id: 'related_person', label: 'Persona relacionada' },
  ];

  if (isLoadingListGuests) {
    return <LoadingLottie />;
  }

  return (
    <RequireAuth>
      <div className='my-6 flex flex-col gap-6 min-h-screen'>
        <AdminSectionTitle
          title='Listas'
        />
        <div className='glass-surface flex flex-col gap-6'>
          <h1 className='text-2xl font-medium'>Lista de invitados</h1>
          <Button
            onClick={() => {
              setIsModalOpen(true);
            }}
            variant='secondary'>
            <div className='flex flex-row gap-2 items-center'>
              <FiUpload />
              <p>Cargar invitados</p>
            </div>
          </Button>
          {listGuests && listGuests.length > 0 ? <FilterTable
            rows={rows}
            headCells={headCells}
            tableId='event-list-guests'
            pagination={{ enabled: true, pageSize: 20 }}
          /> : (
            <EmptyTable
              title='No hay invitados'
              info='No hay invitados en la lista'
              icon={FiArchive}
              iconColor='text-primary-600'
            />
          )}
        </div>
        <CustomModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          hideCancelButton
        >
          <form onSubmit={handleSubmit(handleSubmitAddGuests)} className='flex flex-col gap-4 min-w-[60vw] md:min-w-[30vw]'>
            <h1>Carga masiva de invitados</h1>
            <Controller
              name='guests'
              control={control}
              render={({ field }) => (
                <FormControlTextArea
                  id='guests'
                  label='Invitados *'
                  placeholder='Listar nombres separados por un enter'
                  labelAbove
                  rows={10}
                  {...field}
                />
              )}
            />
            <Controller
              name='related_person'
              control={control}
              render={({ field }) => (
                <Input
                  id='related_person'
                  label='Relacionados a'
                  labelAbove
                  placeholder='Ingrese a quien quiere relacionar a esa importación'
                  {...field}
                />
              )}
            />
            <div className='flex flex-row gap-2'>
              <Button type='button' variant='secondary' onClick={() => setIsModalOpen(false)}>Cancelar</Button>
              <Button type='submit' loading={isCreatingListGuests}>Crear</Button>
            </div>
          </form>
        </CustomModal>
      </div>
    </RequireAuth>
  );
};

export default EventListGuests;
